import { EntitySearchResult } from 'components'
import { action, computed, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { FetchOptions } from 'services'
import { getMainImageSrcForObj } from '@touche-lts/panel-ui/dist'

export class EntitySearchBase<T = any> {
  private loadEntities: () => Promise<void>
  private searchedPropertySelector: (entity: T) => string
  private resultNameSelector: (entity: T) => string = null
  private onEntityActionButtonClick: (entity: T) => void
  private entityActionButtonLabel: string

  constructor () {
    makeObservable(this, {
      searchInput: observable,
      areEntitiesLoaded: observable,
      setSearchInput: action,
      setAreEntitiesLoaded: action,
      clearStore: false,
      setArguments: false,
      onSearchInputChange: false,
      searchResults: computed,
      searchResultsFiltered: observable,
      noResultsMessage: computed,
      entitiesList: computed
    })
  }

  // OBSERVABLES
  public searchInput: string = ''
  public areEntitiesLoaded: boolean = false

  // ACTIONS
  public setSearchInput = (value: string): void => {
    this.searchInput = value
  }

  public setAreEntitiesLoaded = (value: boolean): void => {
    this.areEntitiesLoaded = value
  }

  // METHODS
  public clearStore = (): void => {
    this.setSearchInput('')
    this.setAreEntitiesLoaded(false)
  }

  public setArguments = (args: {
    loadEntities: (options?: FetchOptions) => Promise<void>
    // entitiesList: T[]
    searchedPropertySelector: (entity: T) => string
    resultNameSelector?: (entity: T) => string
    onEntityActionButtonClick: (entity: T) => void
    entityActionButtonLabel?: string
  }): void => {
    this.loadEntities = async (): Promise<void> => {
      await args.loadEntities({ silentProcess: true })
      this.setAreEntitiesLoaded(true)
    }
    // this.entitiesList = args.entitiesList
    this.searchedPropertySelector = args.searchedPropertySelector
    this.resultNameSelector = args.resultNameSelector || null
    this.onEntityActionButtonClick = args.onEntityActionButtonClick
    this.entityActionButtonLabel = args.entityActionButtonLabel || 'Add'
  }

  public onSearchInputChange = (value: string): void => {
    if (!this.areEntitiesLoaded) this.loadEntities()
    this.setSearchInput(value)
  }

  // COMPUTED
  public get searchResults (): T[] {
    if (!this.searchInput) return null
    const searchValue = this.searchInput.toLowerCase().trim()
    return this.entitiesList.filter(entity =>
      this.searchedPropertySelector(entity)
        .toLowerCase()
        .includes(searchValue)
    )
  }

  public searchResultsFiltered = computedFn(
    (entitiesToExclude: T[]): EntitySearchResult[] => {
      if (!this.searchResults) return null
      const entityIdsToExclude = entitiesToExclude.map(
        (entity: any) => entity.id
      )
      return this.searchResults
        .filter((entity: any) => !entityIdsToExclude.includes(entity.id))
        .map((entity: any) => ({
          id: entity.id,
          imageSrc: getMainImageSrcForObj(entity),
          name: this.resultNameSelector
            ? this.resultNameSelector(entity)
            : entity.name || '',
          buttonProps: {
            children: this.entityActionButtonLabel,
            onClick: () => this.onEntityActionButtonClick(entity)
          }
        }))
    }
  )

  public get noResultsMessage (): string {
    return this.areEntitiesLoaded ? 'No math found' : 'Loading...'
  }

  public get entitiesList (): T[] {
    return []
  }
}
